import type {
  ImageBackend,
  ValidationResult,
} from "@unique-wool/specs-validation";
import {
  createBrowserImageBackend,
  validateDecorations,
  validateFabrics,
  validatePositions,
  validateProducts,
} from "@unique-wool/specs-validation";
import { extractProductSnapshotFromCatalog } from "./catalog";
import { ObservableModelRoot } from "./observable";
import { ProductModel } from "./ProductModel";
import { DEFAULT_PRODUCT_VERSION_STATUS } from "./productMetadata/productMetadataEnums";
import type {
  SpecsCatalog,
  SpecsModelSnapshot,
  SpecsModelValidationContext,
} from "./types";

type SpecsModelValidationOptions = {
  imageBackend: ImageBackend;
};

export class SpecsModel extends ObservableModelRoot {
  product!: ProductModel;
  private validationContext!: SpecsModelValidationContext;

  constructor() {
    super("window.specs.model");
    this.defineObservableValue("product", new ProductModel());
    this.validationContext = {
      fabrics: [],
    };
  }

  restore(snapshot: SpecsModelSnapshot) {
    this.product.restore(snapshot);
    return this;
  }

  save(): SpecsModelSnapshot {
    return this.product.save();
  }

  duplicate({
    sourceVersionName,
    productName,
    productDisplayName,
    versionName,
  }: {
    sourceVersionName: string;
    productName: string;
    productDisplayName: string;
    versionName: string;
  }): SpecsModelSnapshot {
    const snapshot = this.save();
    const sourceVersion = snapshot.versions.find(
      (version) => version.name === sourceVersionName
    );

    if (!sourceVersion) {
      throw new Error(
        `Cannot duplicate missing version "${sourceVersionName}" from current specs model`
      );
    }

    return {
      ...snapshot,
      name: productName,
      displayName: productDisplayName,
      defaultVersion: versionName,
      versions: [
        {
          ...sourceVersion,
          name: versionName,
          status: DEFAULT_PRODUCT_VERSION_STATUS,
        },
      ],
    };
  }

  buildValidationSpecs(
    context: SpecsModelValidationContext = this.validationContext
  ) {
    const snapshot = this.save();

    return {
      families: [],
      fabrics: context.fabrics ?? [],
      products: [snapshot],
    };
  }

  async validate(
    context: SpecsModelValidationContext = this.validationContext,
    options?: Partial<SpecsModelValidationOptions>
  ): Promise<ValidationResult> {
    const specs = this.buildValidationSpecs(context);
    const imageBackend = options?.imageBackend ?? createBrowserImageBackend();
    const issues = [
      ...(await validateFabrics(specs)),
      ...(await validateDecorations(specs)),
      ...(await validatePositions(specs)),
      ...(await validateProducts(specs, {
        imageBackend,
      })),
    ];

    return {
      valid: issues.length === 0,
      issues,
    };
  }

  getVersion(name: string) {
    return this.product.getVersion(name);
  }

  addVersion(name: string) {
    return this.product.addVersion(name);
  }

  renameVersion(previousName: string, nextName: string) {
    const version = this.getVersion(previousName);
    if (!version || !nextName || previousName === nextName) {
      return false;
    }

    version.name = nextName;
    return true;
  }

  setIdentity({
    productName,
    productDisplayName,
  }: {
    productName?: string;
    productDisplayName?: string;
  }) {
    if (productName !== undefined) {
      this.product.name = productName;
    }
    if (productDisplayName !== undefined) {
      this.product.setDisplayName(productDisplayName);
    }
    return this;
  }

  setValidationContext(context: SpecsModelValidationContext) {
    this.validationContext = {
      fabrics: context.fabrics ?? [],
    };
    return this;
  }

  static fromSnapshot(snapshot: SpecsModelSnapshot) {
    return new SpecsModel().restore(snapshot);
  }

  static fromCatalog(
    specs: SpecsCatalog,
    productName: string,
    versionName: string
  ) {
    const snapshot = extractProductSnapshotFromCatalog(
      specs,
      productName,
      versionName
    );

    if (!snapshot) {
      return null;
    }

    return SpecsModel.fromSnapshot(snapshot).setValidationContext({
      fabrics: specs.fabrics,
    });
  }
}
